import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toast } from "react-toastify";
import AuthService from "@/services/auth";
import AdminAuthService from "@/services/admin/auth";
import KYCService from "@/services/kyc";
import { registerSessionExpiredHandler } from "@/utils/axios";
import {
  deleteStore,
  loadStore,
  saveStore,
  deleteAdminStore,
  loadAdminStore,
  saveAdminStore,
} from "@/utils/localStorage";
import { isJwtExpired } from "@/utils/jwt";
import { setClientAuthenticated } from "@/utils/authState";
import { buildSignInUrl } from "@/utils/helpers/authReturn";
import {
  AuthStatus,
  AuthContextValue,
  UserAuthState,
  UserProfile,
} from "@/types";
import { Business } from "@/types/business";
import { TAppUser, TAppUserState } from "@types";

const SESSION_EXPIRED_MESSAGE = "Your session has expired. Please login again.";
const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // every 10 minutes

// Admin pages are a separate identity + session namespace. Only the admin
// boot path runs there; the user /auth/me boot is skipped so a stale user
// cookie can never fire the "session expired" teardown on an admin page.
const ADMIN_ROUTE_PREFIX = "/admin";
const SIGN_IN_ROUTES = new Set(["/auth/sign-in", "/admin/auth/sign-in"]);

// Routes that require an authenticated session. Everything else is public and
// must never bounce a visitor when a session teardown fires.
const PROTECTED_ROUTE_PREFIXES = ["/user", "/admin"];

const isAdminRoute = (pathname: string) =>
  pathname.startsWith(ADMIN_ROUTE_PREFIX);
const isSignInRoute = (pathname: string) => SIGN_IN_ROUTES.has(pathname);
const isProtectedRoute = (pathname: string) =>
  PROTECTED_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

// Router-agnostic navigation helpers. AuthContext must work under both the
// Pages Router and the App Router; the router is only ever read inside effects
// and event handlers (never during render), so browser location is sufficient.
const getCurrentPath = () =>
  typeof window !== "undefined" ? window.location.pathname : "";
const getCurrentHref = () =>
  typeof window !== "undefined"
    ? window.location.pathname + window.location.search
    : "";
const navigateTo = (url: string) => {
  if (typeof window !== "undefined") {
    window.location.assign(url);
  }
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [businessDetails, setBusinessDetails] = useState<Business | null>(null);
  const [isBusinessLoading, setIsBusinessLoading] = useState(false);
  const [status, setStatus] = useState<AuthStatus>("idle");
  const [adminUser, setAdminUser] = useState<TAppUser | null>(null);
  const [adminStatus, setAdminStatus] = useState<AuthStatus>("idle");
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<
    string | null
  >(null);
  const bootedRef = useRef(false);
  const adminBootedRef = useRef(false);
  const expiredHandledRef = useRef(false);

  const runSessionExpiredTeardown = useCallback(() => {
    if (expiredHandledRef.current) return;
    expiredHandledRef.current = true;

    // Best-effort server-side logout (clears cookies server-side).
    AuthService.logout().catch(() => undefined);

    deleteStore();
    setUser(null);

    const pathname = getCurrentPath();

    if (isSignInRoute(pathname)) {
      // Already on a sign-in page — never bounce the user elsewhere or nag
      // with an "expired" toast; they're already where they need to be.
      setStatus("unauthenticated");
      return;
    }

    setStatus("expired");
    setSessionExpiredMessage(SESSION_EXPIRED_MESSAGE);
    //toast.error(SESSION_EXPIRED_MESSAGE);

    // Only bounce off protected pages. On public pages the visitor simply
    // becomes anonymous — never redirect them away from the content they
    // were viewing.
    if (isProtectedRoute(pathname) && pathname !== "/") {
      navigateTo("/");
    }
  }, []);

  // Let the axios 401/refresh interceptor route mid-session expiry into the
  // same teardown used during boot.
  useEffect(() => {
    registerSessionExpiredHandler(runSessionExpiredTeardown);
    return () => registerSessionExpiredHandler(() => undefined);
  }, [runSessionExpiredTeardown]);

  const refreshUser = useCallback(async () => {
    try {
      const response = await AuthService.getMe();
      const profile = response?.data?.data;
      if (profile) {
        setUser(profile);
        setStatus("authenticated");
        setSessionExpiredMessage(null);
        expiredHandledRef.current = false;
        return;
      }
      setStatus("unauthenticated");
    } catch (error) {
      setStatus("unauthenticated");
      throw error;
    }
  }, []);

  // Business details are only relevant for business roles (developer/realtor).
  // Fetched once into the context and refreshed on demand so consumers don't
  // hit /business/details on every mount.
  const refreshBusinessDetails = useCallback(async () => {
    try {
      setIsBusinessLoading(true);
      const response = await KYCService.getBusinessDetails();
      const details = response?.data?.data;
      if (details) {
        setBusinessDetails(details);
        return;
      }
      setBusinessDetails(null);
    } catch (error) {
      setBusinessDetails(null);
      throw error;
    } finally {
      setIsBusinessLoading(false);
    }
  }, []);

  // Admin identity boot (GET /admin/auth/me). Runs on /admin/* routes. A
  // cookie hint (or a live admin store token) triggers the network call; on
  // success the fresh profile is persisted into the admin store so the legacy
  // useAdminAuthToken / AdminWrapper consumers keep working unchanged.
  const initializeAdmin = useCallback(async () => {
    if (adminBootedRef.current) return;
    adminBootedRef.current = true;

    setAdminStatus("loading");

    const hasAdminHint =
      typeof document !== "undefined" &&
      (document.cookie.includes("admin_is_logged_in=true") ||
        document.cookie.includes("is_logged_in=true"));

    if (!hasAdminHint) {
      const stored = loadAdminStore();
      if (stored?.token && !isJwtExpired(stored.token)) {
        setAdminUser(stored.user);
        setAdminStatus("authenticated");
      } else {
        setAdminStatus("unauthenticated");
      }
      return;
    }

    try {
      const response = await AdminAuthService.getMe();
      const profile = response?.data?.data;
      if (profile) {
        const stored = loadAdminStore();
        saveAdminStore({
          token: stored?.token ?? "admin-session",
          user: profile,
        });
        setAdminUser(profile);
        setAdminStatus("authenticated");
        return;
      }
      setAdminStatus("unauthenticated");
    } catch {
      // Invalid/expired admin session. On a sign-in page the user simply stays
      // put; protected admin pages are guarded by AdminWrapper instead.
      deleteAdminStore();
      setAdminUser(null);
      setAdminStatus("unauthenticated");
    }
  }, []);

  const initialize = useCallback(async () => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    const pathname = getCurrentPath();

    // Admin namespace owns a separate session — never boot the *user* identity
    // here. That mismatch is what 401'd /auth/me and bounced admins to the
    // homepage via the expired teardown when they refreshed a sign-in page.
    if (isAdminRoute(pathname)) {
      setStatus("unauthenticated");
      await initializeAdmin();
      return;
    }

    setStatus("loading");

    // Always verify the session via /auth/me. The JS-visible is_logged_in
    // cookie hint is unreliable (it's subdomain-scoped on staging/prod and may
    // be absent even though the httpOnly session cookies are valid), so the
    // network call is the source of truth. The interceptor refreshes on a 401;
    // if recovery fails we simply land on "unauthenticated" instead of firing
    // the expired teardown, so anonymous visitors aren't toasted/redirected on
    // every page load.
    try {
      await refreshUser();
    } catch {
      setStatus("unauthenticated");
    }
  }, [refreshUser, initializeAdmin]);

  // Boot once on mount.
  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the module-level auth flag (used by non-React code like utils/track)
  // in sync so client-triggered marketing events reach the backend whenever
  // there is an authenticated user session.
  useEffect(() => {
    setClientAuthenticated(status === "authenticated");
  }, [status]);

  // Keep the session alive by refreshing every 10 minutes while authenticated.
  useEffect(() => {
    if (status !== "authenticated") return;

    const refreshTimer = setInterval(async () => {
      try {
        const response = await AuthService.refreshToken();
        if (response?.data?.status === "success") {
          await refreshUser();
        }
      } catch {
        runSessionExpiredTeardown();
      }
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(refreshTimer);
  }, [status, refreshUser, runSessionExpiredTeardown]);

  // Load business details once authenticated for business roles (developer /
  // realtor). Regular users never need this, so skip the network call.
  useEffect(() => {
    if (status !== "authenticated" || !user) {
      setBusinessDetails(null);
      setIsBusinessLoading(false);
      return;
    }
    if (user.role !== "developer" && user.role !== "realtor") {
      setBusinessDetails(null);
      setIsBusinessLoading(false);
      return;
    }
    refreshBusinessDetails().catch(() => undefined);
  }, [status, user, refreshBusinessDetails]);

  const login = useCallback(
    async (payload: { email: string; password: string }) => {
      await AuthService.login(payload);
      await refreshUser();
    },
    [refreshUser],
  );

  const logout = useCallback(async () => {
    try {
      await AuthService.logout();
    } catch {
      // Best-effort — still clear local state if the API is unreachable.
    }
    deleteStore();
    setUser(null);
    setStatus("unauthenticated");
    setSessionExpiredMessage(null);
    navigateTo(buildSignInUrl(getCurrentHref()));
  }, []);

  const updateUser = useCallback((userData: UserAuthState) => {
    if (userData) {
      saveStore(userData as unknown as TAppUserState);
      setUser(userData.user);
      setSessionExpiredMessage(null);
    }
  }, []);

  const updatePhotoURL = useCallback((photoURL: string) => {
    setUser((prev) => {
      const updated = prev ? { ...prev, profile_picture: photoURL } : prev;
      const storeData = loadStore();
      if (storeData && updated) {
        saveStore({
          ...storeData,
          user: updated as unknown as TAppUserState["user"],
        });
      }
      return updated;
    });
  }, []);

  // Merge partial fields into the authenticated user WITHOUT touching the
  // stored token/Bearer, so co-located profile updates (e.g. the listing
  // policy flag) take effect immediately instead of waiting for /auth/me.
  const updateUserField = useCallback((fields: Partial<UserProfile>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...fields };
      const storeData = loadStore();
      if (storeData) {
        saveStore({
          ...storeData,
          user: updated as unknown as TAppUserState["user"],
        });
      }
      return updated;
    });
  }, []);

  const role = user?.role;

  const value: AuthContextValue = {
    user,
    is_logged_in: status === "authenticated",
    status,
    isDeveloper: role === "developer",
    isRealtor: role === "realtor",
    isUser: role === "regular-user",
    adminUser,
    admin_is_logged_in: adminStatus === "authenticated",
    adminStatus,
    businessDetails,
    isBusinessLoading,
    initialize,
    initializeAdmin,
    refreshUser,
    refreshBusinessDetails,
    login,
    logout,
    sessionExpiredMessage,
    updateUser,
    updatePhotoURL,
    updateUserField,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
